import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "../../shared/seo/Seo";
import { urls, copy, currentLang } from "../../shared/seo/links";
import QuestionMainSection from "../../components/questions/QuestionMainSection";
import LawyersPagination from "../../components/lawyers/LawyersPagination";
import PageContent from "../../components/questions/PageContent";
import Heading from "../../components/questions/Heading";
import QuestionPostCard from "../../components/questions/QuestionPostCard";
import SideBar from "../../components/questions/SideBar";
import { useQuestionContext } from "../../shared/context/QuestionContext";
import { useTranslation } from "react-i18next";
import ComponentsLoader from "../../components/includes/ComponentsLoader";
import NotFound from "../NotFound/NotFound";
import LoadError from "../../shared/components/LoadError";
import { LOAD_FAILED, LOAD_NOT_FOUND, loadOutcome, loadWithRetry } from "../../shared/helpers/loadResult";

/**
 * /Questions-Reponses?page=N: the same page of questions as the server
 * renders for that address (POST /question/listing), with page links and a
 * self canonical, so every page of the listing is its own indexable page.
 */
export default function Questions() {
  const lang = currentLang();
  const [searchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  // A failed request (rate limited, server or network down) is not "no
  // question yet": it offers a retry. Only a 404 is the not-found page.
  const [outcome, setOutcome] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const { getQuestionsListing } = useQuestionContext();
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    async function fetchQuestions() {
      setIsLoading(true);
      setOutcome(null);
      const response = await loadWithRetry(() => getQuestionsListing({ lang, page }), () => !cancelled);
      if (cancelled) return;
      const result = loadOutcome(response);
      setOutcome(result);
      if (result !== LOAD_FAILED) {
        setQuestions(response.data?.questions ?? []);
        setTotal(response.data?.total ?? 0);
        setPageSize(response.data?.pageSize ?? 20);
      }
      setIsLoading(false);
    }
    fetchQuestions();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
    // getQuestionsListing is left out: the provider recreates it on every render.
  }, [lang, page, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const failed = outcome === LOAD_FAILED;
  const loaded = !isLoading && !failed;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  // A page past the last one is a 404 on the server too.
  if (outcome === LOAD_NOT_FOUND || (loaded && page > pages)) return <NotFound />;

  const text = copy.questions(lang, { page });
  const pathFor = (p) => urls.withPage(urls.sectionPath("questions", lang), p);

  return (
    <>
      <Heading />
      <PageContent>
        <Seo
          title={text.title}
          description={text.description}
          path={pathFor(page)}
          alternates={urls.alternates((l) => urls.withPage(urls.sectionPath("questions", l), page))}
        />
        <QuestionMainSection>
          {isLoading ? (
            <ComponentsLoader page />
          ) : failed ? (
            <LoadError onRetry={() => setAttempt((n) => n + 1)} />
          ) : questions.length > 0 ? (
            questions.map((question) => (
              <QuestionPostCard key={question._id} question={question} />
            ))
          ) : (
            <div className="avc-empty">
              <span className="avc-empty__icon">
                <i className="far fa-comments" aria-hidden="true"></i>
              </span>
              <h2 className="avc-empty__title">{t("qa_empty")}</h2>
            </div>
          )}
          {loaded && (
            <div className="col-12">
              <LawyersPagination page={page} pages={pages} pathFor={pathFor} />
            </div>
          )}
        </QuestionMainSection>
        <SideBar />
      </PageContent>
    </>
  );
}
